// A submission that takes time: one spinner, and never a second submission.
//
// Most forms here post and re-render. Some answer in milliseconds (a filter, a flag), others go and
// compute — twelve months of Orekit for a sky tool, an outbound call for a BYOK tool. Until the new
// page paints, the button looks exactly as it did before the click: nothing says the click landed.
// The reader concludes it did not, and clicks again — which is a second POST, a second computation,
// and on a write path a second act.
//
// Two decisions carry the module:
//
//   1. THE DELAY IS THE FILTER. The spinner appears only after the submission has lasted longer
//      than a page normally takes to come back. A fast form never flashes one, so nothing has to be
//      tagged as "slow": the same code rides on every form and only the slow ones show anything.
//
//   2. THE GUARD IS IMMEDIATE. It does not wait for the delay: from the first submit event the form
//      is marked, and any further submission of that same form is refused. Disabling the button is
//      what the reader sees, but it is not what holds the door.
//
// Progressive: with JavaScript off, forms submit as they always did. What is lost is the spinner and
// the guard, not the submission.

const SPINNER_DELAY_MS = 300;

const SPINNER_CLASS = 'fa-solid fa-spinner fa-spin fa-fw';

/** Every button that can send this form — a form may offer several gestures of equal rank. */
function submitters(form) {
	return [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];
}

/**
 * Shows the wait on the buttons: the icon becomes a spinner, and the button closes.
 *
 * <p>Closing the button here rather than at submit time is deliberate — the form data has long been
 * serialised by now, so disabling can no longer drop a submitter's own name and value from the POST.
 */
function showWaiting(form) {
	if (form.dataset.busy !== 'true') return;

	submitters(form).forEach((button) => {
		const icon = button.querySelector('.ui-icon i');
		if (icon) {
			// The original classes travel on the element itself: putting them back is then a copy,
			// with nothing to remember on our side.
			icon.dataset.busyRestore = icon.className;
			icon.className = SPINNER_CLASS;
		} else {
			button.insertAdjacentHTML('afterbegin',
				`<span class="ui-icon" data-busy-icon><i class="${SPINNER_CLASS}"></i></span>`);
		}
		button.disabled = true;
	});
}

/** Puts the buttons back the way the page rendered them. */
function clearWaiting(form) {
	delete form.dataset.busy;

	submitters(form).forEach((button) => {
		const icon = button.querySelector('.ui-icon i[data-busy-restore]');
		if (icon) {
			icon.className = icon.dataset.busyRestore;
			delete icon.dataset.busyRestore;
		}
		const injected = button.querySelector('.ui-icon[data-busy-icon]');
		if (injected) injected.remove();
		button.disabled = false;
	});
}

// Bubble phase on the document, for the same reason as hold-position.js: it runs AFTER any listener
// the form carries, so `defaultPrevented` tells us the submission was taken over by something that
// does not navigate — an AJAX form, an editor posting on its own. Those answer without leaving the
// page, and a spinner nobody would clear is worse than no spinner.
document.addEventListener('submit', (event) => {
	const form = event.target;
	if (!(form instanceof HTMLFormElement)) return;
	if (event.defaultPrevented) return;
	if (form.hasAttribute('data-ajax')) return;
	if (form.dataset.submitBusy === 'off') return;

	// A submission that opens elsewhere leaves this page usable, so its buttons must stay usable too.
	const target = (event.submitter && event.submitter.getAttribute('formtarget')) || form.getAttribute('target');
	if (target && target !== '_self') return;

	// The door, and it shuts before anything is drawn: between the click and the delay there is no
	// disabled button yet, and that is exactly the window a double click falls into.
	if (form.dataset.busy === 'true') {
		event.preventDefault();
		return;
	}
	form.dataset.busy = 'true';

	window.setTimeout(() => showWaiting(form), SPINNER_DELAY_MS);
});

// Coming BACK to the page — the reader hit the back button and the browser served its own copy,
// buttons frozen mid-submission and all. That page is alive again and must be usable: a form left
// closed here would refuse every further attempt, with no way to reopen it but a reload.
window.addEventListener('pageshow', (event) => {
	if (!event.persisted) return;
	document.querySelectorAll('form[data-busy="true"]').forEach(clearWaiting);
});
